import Vue from 'vue';
import resetState from '@/helpers/resetState';
import { getTransactionPromise } from '@/helpers/transaction';
import store from '@/store';
import { ABI } from './ABI';
import { formatData } from '@/constants/commonContent';
import { messages } from '@/constants/messages';
import { Contract } from 'web3-eth-contract';

const FEATURE_NAME = 'VotingFeature';

let from;

async function contract(): Promise<Contract> {
  from = {from: store.getters.getWallet};
  const address = store.getters.getFeatureAddress(FEATURE_NAME);
  return new Promise(resolve => {
    let contract = new window.web3.eth.Contract(ABI, address);
    resolve(contract);
  });
}

const initialState = {
  percentsForAccept: null,
  votings: {},
  votingCounter: 0,
  votes: {},
  votingVotes: {},
  voteCounter: 0,
  tempVote: { subject: '', description: '', endTime: '' }
};

const state = {
  percentsForAccept: initialState.percentsForAccept,
  votings: initialState.votings,
  votingCounter: initialState.votingCounter,
  votes: initialState.votes,
  votingVotes: initialState.votingVotes,
  voteCounter: initialState.voteCounter,
  tempVote: initialState.tempVote
};

const getters = {
  getPercentsForAccept: state => state.percentsForAccept,
  getVotingCounter: state => state.votingCounter,
  getVoteCounter: state => state.voteCounter,
  getVotings: state => state.votings,
  getVotes: state => state.votes,
  getVote: state => id => state.proposals[id],
  getVotingVotes: state => id => state.votingVotes[id],
  getVotePercent: (state, getters) => {
    let balance = getters.getTokenBalance();
    let allBalance = getters.getTokenTotalSupply - getters.getCompanyBalance;
    let percent = ((balance / allBalance) * 100).toFixed(2);
    percent = percent === '0.00' && balance > 0 ? (percent = '0.01') : percent;
    percent = percent === '100.00' && balance < allBalance ? (percent = '99.99') : percent;

    return percent;
  }
};

const mutations = {
  setPercentsForAccept: (state, item) => {
    state.percentsForAccept = item;
  },
  setVotingCounter: (state, item) => {
    state.votingCounter = item;
  },
  setVoting: (state, item) => {
    Vue.set(state.votings, item.id, item);
  },
  setVote: (state, item) => {
    Vue.set(state.votes, item.votingId, item);
  },
  resetVotingFeature: state => {
    resetState(state, initialState);
  }
};

const actions = {
  initVotingFeature({ dispatch, commit, getters }) {
    dispatch('getVotingsDapp');
    dispatch('getVotesDapp');
    dispatch('getPercentsForAccept');

    //Events
    contract().then(contract => {
      let transactionHash = '0x';
      contract.events.VoteEvent({ addr: getters.getWallet }).subscribe((err, res) => {
        if (!err && transactionHash !== res.transactionHash) {
          transactionHash = res.transactionHash;
          dispatch('getVotesDapp');
          commit('newVotePending', null);
        }
        commit('resetMailPending', 'newVoting');
      });
      contract.events.VotingEvent().subscribe((err, res) => {
        if (!err && transactionHash !== res.transactionHash) {
          transactionHash = res.transactionHash;
          dispatch('getVotingsDapp');
          commit('newVotingPending', false);
        }
        commit('resetMailPending', 'resolveVoting');
      });
    });
  },
  /**
   * Create voting for members
   * @param subject {string}
   * @param description {string}
   * @param deadlineTime {number}
   */
  createVotingDapp({ commit, dispatch }, data) {
    contract().then(contract => {
      contract.methods.createVoting(data.subject, data.description, data.deadlineTime)
        .send(from, (err, res) => {
          if (err) {
            commit('newVotingPending', false);
            Vue.toasted.error(messages.canceledVoting);
          } else {
            dispatch('sendNewVoteMailExt', {
              subject: data.subject,
              description: data.description,
              endTime: Vue.moment.unix(data.deadlineTime).format(formatData.dateTime),
              tx: res
            });
          }
        });
    });
  },
  /**
   * Send vote
   * @param votingId {number}
   * @param verdict {string}
   */
  voteDapp({ commit }, data) {
    contract().then(contract => {
      contract.methods.vote(data.votingId, data.verdict)
        .send(from,(err) => {
          if (err) {
            Vue.toasted.error(messages.canceledVote);
            commit('newVotePending', null);
          }
        });
    });
  },
  /**
   * Is voted
   * @param address {number}
   * @param votingId {number}
   * @return {Promise<Boolean>}
   */
  isVotedDapp({ getters }, votingId) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.isVoted(getters.getWallet, votingId)
          .call()
          .then(res => {
            resolve(res);
          })
          .catch(console.error);
      });
    });
  },
  /**
   * Resolve voting
   * @param id {number}
   */
  resolveVotingDapp({ commit, dispatch, getters }, voting) {
    contract().then(contract => {
      contract.methods.resolveVoting(voting.id)
        .send(from, (err, res) => {
          if (err) {
            console.error('Voting has not resolved!');
            commit('newVotePending', null);
          } else {
            dispatch('sendResolveVotingMailExt', {
              subject: voting.subject,
              resolveTime: Vue.moment().format(formatData.dateTime),
              tx: res
            });
          }
        });
    });
  },
  /**
   * Get voting
   * @param id {number}
   * @res[0] "subject" {string}
   * @res[1] "description" {string}
   * @res[2] "creator" {string}
   * @res[3] "createdTime" {number}
   * @res[4] "deadlineTime" {number}
   * @res[5] "resolvedTime" {number}
   * @res[6] "passed" {string}
   * @res[7] "voteSum" {number}
   * @res[8] "resolvedAddr" {string}
   */
  getVotingDapp({ commit }, id) {
    contract().then(contract => {
      contract.methods.getVoting(id)
        .call()
        .then(res => {
            commit('setVoting', {
            id,
            subject: res[0],
            description: res[1],
            creator: res[2],
            createdTime: parseInt(res[3]),
            deadlineTime: parseInt(res[4]),
            resolvedTime: parseInt(res[5]),
            passed: res[6],
            voteSum: parseInt(res[7]),
            resolvedAddr: res[8]
          });
        })
        .catch(console.error);
    });
  },
  getVotingsDapp({ dispatch }) {
    dispatch('getVotingCounterDapp').then(result => {
      for (let i = 0; i < result; i++) {
        dispatch('getVotingDapp', i);
      }
    });
  },
  /**
   * Get voting count
   * @return {Promise<number>}
   * @res "count" {number}
   */
  getVotingCounterDapp({ commit }) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getVotingCounter()
          .call()
          .then(res => {
            commit('setVotingCounter', res);
            resolve(res);
          })
          .catch(console.error);
      });
    });
  },
  /**
   * Get count of  votes in voting
   * @res "count" {number}
   */
  getVotesDapp({ dispatch, commit }) {
    contract().then(contract => {
      contract.methods.getVotes()
        .call()
        .then(res => {
          if (res) {
            res.forEach(id => {
              dispatch('getVoteDapp', parseInt(id)).then(res => {
                commit('setVote', res);
              });
            });
          }
        })
        .catch(console.error);
    });
  },
  /**
   * Get voting of votes
   * @return {Promise<Array<Object>>}
   */
  getVotingVotesDapp({ dispatch }, votingId) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getVotingVotes(votingId)
          .call()
          .then(async res => {
            if (res) {
              let arr: any = [];
              for (let id of res) {
                await dispatch('getVoteDapp', parseInt(id)).then(res => {
                  arr.push(res);
                });
              }
              resolve(arr);
            }
          })
          .catch(console.error);
      });
    });
  },
  /**
   * Get vote
   * @param id {number}
   * @return {Promise<Object>}
   * @res[0] "voter" {string}
   * @res[1] "votingId" {number}
   * @res[2] "voterBalance" {number}
   * @res[3] "createdTime" {number}
   */
  getVoteDapp({}, id) {
    return new Promise(resolve => {
      contract().then(contract => {
        contract.methods.getVote(id)
          .call()
          .then(res => {
            resolve({
              id,
              voter: res[0],
              votingId: parseInt(res[1]),
              voterBalance: parseInt(res[2]),
              createdTime: parseInt(res[3])
            });
          })
          .catch(console.error);
      });
    });
  },
  /**
   * Get percents for accept
   * @res "percents" {number}
   */
  getPercentsForAccept({ commit }) {
    contract().then(contract => {
      contract.methods.getPercentsForAccept()
        .call()
        .then(res => {
          commit('setPercentsForAccept', res);
        })
        .catch(console.error);
    });
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
